import { useMemo, useState } from 'react';

import { Close, ContentPasteGo } from '@mui/icons-material';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { format, parseISO } from 'date-fns';
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_SortingState,
  useMaterialReactTable,
} from 'material-react-table';
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';
import { useQuery } from 'react-query';
import {
  getRunFidcHistory,
  RunFidcHistoryApiResponse,
} from 'src/services/run-fidc';

import { Totalizations } from '../Totalizations';
import {
  MRTTableConfig,
  parseDataBySeparatorType,
  parseTableDataToLinesAndSeparator,
  setColumnsByData,
} from '../utils';

type HistoryProps = {
  open: boolean;
  onClose: () => void;
};

export function HistoryDialog({ open, onClose }: HistoryProps) {
  const [sorting, setSorting] = useState<MRT_SortingState>([
    { id: 'processedAt', desc: true },
  ]);
  const [totalizationsOpen, setTotalizationsOpen] = useState(false);
  const [tableConfig, setTableConfig] = useState<MRTTableConfig | null>(null);

  const { data: history, isLoading: isLoadingHistory } = useQuery({
    queryKey: ['run-fidc-history'],
    queryFn: () => getRunFidcHistory(),
    enabled: open,
  });

  function ProcessTableConfig(tableData: string): void {
    const { lines, separator } = parseTableDataToLinesAndSeparator(
      String(tableData),
    );

    const parsed = parseDataBySeparatorType(lines, separator);
    const config = setColumnsByData(parsed);
    setTableConfig(config);
    setTotalizationsOpen(true);
  }

  const columns = useMemo<MRT_ColumnDef<RunFidcHistoryApiResponse>[]>(
    () => [
      {
        header: 'Exibir',
        accessorKey: 'view',
        size: 80,
        enableSorting: false,
        Cell: ({ row }) => {
          const { content } = row.original;
          return (
            <IconButton
              onClick={() => {
                ProcessTableConfig(content ?? '');
              }}>
              <ContentPasteGo />
            </IconButton>
          );
        },
      },
      {
        header: 'Data',
        accessorKey: 'processedAt',
        Cell: ({ row }) => {
          return (
            <Typography>
              {format(
                parseISO(row.original?.processedAt ?? ''),
                'dd/MM/yyyy HH:mm:ss',
              )}
            </Typography>
          );
        },
      },
    ],
    [],
  );

  const table = useMaterialReactTable({
    columns,
    data: history ?? [],
    localization: MRT_Localization_PT_BR,
    enableTopToolbar: false,
    enableColumnActions: false,
    enableBottomToolbar: true,
    enableGlobalFilterModes: true,
    enablePagination: true,
    enableRowNumbers: false,
    enableRowVirtualization: true,
    muiTableContainerProps: {
      sx: {
        maxHeight: 350,
        minHeight: 350,
        minWidth: {
          xs: '100%',
          sm: '100%',
          md: '400px',
        },
      },
    },
    state: {
      columnOrder: ['view', 'createdAt'],
      sorting,
    },
    onSortingChange: setSorting,
  });
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle sx={{ textAlign: 'center', fontSize: '24px' }}>
        Histórico
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={(theme) => ({
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}>
        <Close />
      </IconButton>
      <DialogContent>
        <MaterialReactTable table={table} />
        <Totalizations
          title="Histórico"
          open={totalizationsOpen}
          onClose={() => setTotalizationsOpen(false)}
          isLoading={isLoadingHistory}
          tableConfig={tableConfig}
        />
      </DialogContent>
    </Dialog>
  );
}
